import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, House, XCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", payment_status: null, attempts: 0, plan: null });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", payment_status: null, plan: null }); return; }
    let cancelled = false;

    const poll = async (attempts = 0) => {
      if (cancelled) return;
      if (attempts >= 8) { setState((s) => ({ ...s, status: "timeout" })); return; }
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", plan: data.plan, attempts });
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", payment_status: data.payment_status, plan: data.plan, attempts });
          return;
        }
        setState((s) => ({ ...s, payment_status: data.payment_status, attempts }));
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (e) {
        setState({ status: "error", payment_status: null, plan: null, attempts });
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div className="min-h-[60vh] grid place-items-center" data-testid="billing-success">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full rounded-3xl border border-sand-300 bg-white p-10 shadow-ambient text-center">
        {state.status === "paid" ? (
          <>
            <CheckCircle weight="duotone" size={72} className="mx-auto text-sage-600" />
            <h1 className="mt-5 font-heading text-3xl font-semibold text-stoneink-900">You&apos;re in.</h1>
            <p className="mt-2 text-stoneink-700">Welcome to {state.plan === "family" ? "Family" : "Pro"}. Make yourself at home.</p>
            <Link to="/" data-testid="back-home-btn" className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium">
              <House weight="duotone" /> Back to home
            </Link>
          </>
        ) : state.status === "checking" ? (
          <>
            <div className="mx-auto h-16 w-16 rounded-full border-4 border-terracotta-500 border-t-transparent animate-spin" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-stoneink-900">Confirming your payment…</h1>
            <p className="mt-2 text-stoneink-500 text-sm">Attempt {state.attempts + 1}/8</p>
          </>
        ) : (
          <>
            <XCircle weight="duotone" size={64} className="mx-auto text-destructive" />
            <h1 className="mt-5 font-heading text-2xl font-medium text-stoneink-900">Something didn&apos;t go through.</h1>
            <p className="mt-2 text-stoneink-700">Status: {state.status}{state.payment_status ? ` (${state.payment_status})` : ""}</p>
            <Link to="/pricing" data-testid="retry-pricing-btn" className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-stoneink-900 text-white font-medium">
              Try again
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
